<template>
    <div class="attachment-preview-item">
        <div v-if="loading" class="attachment-preview-loading">
            <i class="el-icon-loading"></i> 附件加载中…
        </div>
        <template v-else-if="isPdf && previewSrc">
            <embed
                :src="previewSrc"
                type="application/pdf"
                class="attachment-preview-pdf"
            />
            <div class="attachment-preview-toolbar">
                <a :href="url" target="_blank" rel="noopener noreferrer">在新窗口打开</a>
                <span v-if="useDirectUrl" class="attachment-preview-hint">（CDN 未开放跨域，已使用直链预览）</span>
            </div>
        </template>
        <img v-else-if="isImage" :src="url" class="attachment-preview-img" alt="" />
        <div v-else class="attachment-preview-fallback">
            <a :href="url" target="_blank" rel="noopener noreferrer">点击查看附件</a>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AttachmentPreviewItem',
    props: {
        url: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            previewSrc: '',
            blobUrl: '',
            useDirectUrl: false,
        };
    },
    computed: {
        isPdf() {
            return this.fileUrlPath(this.url).endsWith('.pdf');
        },
        isImage() {
            return /\.(jpe?g|png|gif|webp|bmp)$/.test(this.fileUrlPath(this.url));
        },
    },
    watch: {
        url: {
            immediate: true,
            handler() {
                this.preparePreview();
            },
        },
    },
    beforeDestroy() {
        this.revokeBlob();
    },
    methods: {
        fileUrlPath(url) {
            if (!url) return '';
            return String(url).split('?')[0].toLowerCase();
        },
        buildPdfViewUrl(rawUrl) {
            const base = String(rawUrl || '').split('#')[0];
            return base + '#toolbar=0&navpanes=0&view=FitH';
        },
        /** CDN / 第三方域名未配置 CORS，fetch 会失败；embed 直链仍可预览 */
        shouldUseDirectPdfUrl(url) {
            if (!url) {
                return true;
            }
            const str = String(url).trim();
            if (str.startsWith('blob:') || str.startsWith('data:')) {
                return false;
            }
            try {
                const parsed = new URL(str, window.location.href);
                if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
                    return true;
                }
                return parsed.origin !== window.location.origin;
            } catch (e) {
                return true;
            }
        },
        revokeBlob() {
            if (this.blobUrl) {
                URL.revokeObjectURL(this.blobUrl);
                this.blobUrl = '';
            }
        },
        async preparePreview() {
            this.revokeBlob();
            this.previewSrc = '';
            this.useDirectUrl = false;
            if (!this.url) {
                return;
            }
            if (!this.isPdf) {
                return;
            }
            const directSrc = this.buildPdfViewUrl(this.url);
            if (this.shouldUseDirectPdfUrl(this.url)) {
                this.previewSrc = directSrc;
                this.useDirectUrl = true;
                return;
            }
            this.loading = true;
            try {
                const res = await fetch(this.url, { mode: 'cors' });
                if (res.ok) {
                    const blob = await res.blob();
                    this.blobUrl = URL.createObjectURL(blob);
                    this.previewSrc = this.buildPdfViewUrl(this.blobUrl);
                } else {
                    this.previewSrc = directSrc;
                    this.useDirectUrl = true;
                }
            } catch (e) {
                this.previewSrc = directSrc;
                this.useDirectUrl = true;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.attachment-preview-item {
    width: 100%;
    min-height: 120px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
}
.attachment-preview-pdf {
    display: block;
    width: 100%;
    height: 720px;
    border: none;
    background: #fff;
}
.attachment-preview-img {
    display: block;
    width: 100%;
    max-height: 900px;
    object-fit: contain;
    background: #fff;
}
.attachment-preview-loading,
.attachment-preview-fallback {
    padding: 48px 16px;
    text-align: center;
    color: #6a7282;
    font-size: 14px;
    line-height: 24px;
}
.attachment-preview-fallback a {
    color: #e7000b;
    text-decoration: none;
}
.attachment-preview-toolbar {
    padding: 8px 12px;
    text-align: right;
    font-size: 12px;
    line-height: 20px;
    background: #f3f4f6;
    border-top: 1px solid #e5e7eb;
}
.attachment-preview-toolbar a {
    color: #e7000b;
    text-decoration: none;
}
.attachment-preview-hint {
    margin-left: 8px;
    color: #99a1af;
}
</style>
